"use client";
import { colors } from "@areahub/theme";
import {
  Flex,
  Heading,
  ListItem,
  Text,
  UnorderedList,
  useMediaQuery,
} from "@chakra-ui/react";
import { Button, Button_T } from "@ui/components";
import { ArrowBanner, ArrowProps } from "@ui/components/arrow-banner";
import { SectionTemplate } from "components/individuals/blocks";
import { ButtonProp } from "lib/gcms";

export interface EnterpriseArrowBannersSectionProps {
  title: string;
  subtitle: string;
  bg: string;
  titleColor: string;
  subtitleColor: string;
  arrowBanners: ArrowProps[];
  buttons: Omit<ButtonProp, "buttonAction" | "id">[];
}

export const EnterpriseArrowBannersSection = ({
  title,
  subtitle,
  bg,
  titleColor,
  subtitleColor,
  arrowBanners,
  buttons,
}: EnterpriseArrowBannersSectionProps) => {
  const [isLargeBp] = useMediaQuery("(min-width: 992px)");
  return (
    <SectionTemplate bg={bg}>
      <Heading
        as={"h1"}
        color={titleColor}
        fontSize={{
          base: 26,
          md: 34,
          lg: 38,
          xl: 45,
        }}
        fontWeight={"bold"}
        lineHeight={{
          base: 1.1,
          lg: 1.2,
        }}
        mb={{
          base: 6,
          lg: 8,
        }}
        w={{
          base: "full",
          lg: "90%",
          xl: "75%",
        }}
      >
        {title}
      </Heading>
      <Heading
        as={"h2"}
        color={subtitleColor}
        fontSize={{
          base: 18,
          lg: 20,
          xl: 22,
        }}
        fontWeight={"bold"}
        pb={{
          base: 8,
          lg: 12,
        }}
      >
        {subtitle}
      </Heading>
      <Flex justifyContent={"center"} flexDir={"column"}>
        <Flex
          flexDir={"row"}
          gap={{
            base: 3,
            sm: 5,
            md: 10,
            lg: 14,
            xl: 20,
          }}
          w={{
            base: "91%",
            md: "93%",
          }}
          pb={{
            base: 6,
            lg: "unset",
          }}
        >
          {arrowBanners.map((arrow, idx) => (
            <ArrowBanner key={idx} {...arrow} bg={bg} zIndex={5 - idx} />
          ))}
        </Flex>
        {isLargeBp ? (
          <Flex
            flexDir={"row"}
            gap={{
              lg: 28,
            }}
            pt={{
              md: 6,
              lg: 10,
            }}
            w={{
              lg: "95%",
            }}
            mx={"auto"}
            pr={{ lg: 10 }}
          >
            {arrowBanners.map((arrow, idx) => (
              <Text
                key={idx}
                color={arrow.descriptionColor}
                fontSize={{
                  base: 16,
                  lg: 17,
                  xl: 18,
                }}
                pl={{
                  lg: idx === 1 ? 6 : "",
                }}
              >
                {arrow.description}
              </Text>
            ))}
          </Flex>
        ) : (
          <>
            <UnorderedList spacing={3} color={subtitleColor}>
              {arrowBanners.map((arrow, idx) => (
                <ListItem key={idx} fontSize={16}>
                  {arrow.description}
                </ListItem>
              ))}
            </UnorderedList>
          </>
        )}
      </Flex>
      <Flex
        mt={{
          base: 10,
          md: 20,
        }}
        justify={"center"}
      >
        {buttons.map((btn, index) => (
          <Button
            key={`arrow-section-btn-${index}`}
            bgColor={colors.enterpriseColors.blue["50"]}
            color={bg}
            fontSize={{
              base: 12,
              md: 14,
              lg: 16,
              xl: 18,
            }}
            fontWeight={"bold"}
            py={{
              base: "6",
              lg: "8",
            }}
            w={{
              base: "140px",
              md: "160px",
              lg: "180px",
              xl: "220px",
            }}
            textTransform={"uppercase"}
            {...(btn as Button_T)}
          />
        ))}
      </Flex>
    </SectionTemplate>
  );
};
